import { collection, doc, getDocFromServer, getDocsFromServer, getFirestore, query, setDoc, Timestamp, where } from "firebase/firestore";
import { customerAuth } from "./customerAuth";
import { normaliseProduct } from "./catalog";
import { buildOrder, deliveryCharge } from "./checkoutModel";

// Use the customer app's Auth context, never the default Admin Firestore client.
function database(uid) {
  if (!uid || customerAuth?.currentUser?.uid !== uid) throw new Error("Your login changed. Please reopen checkout.");
  return getFirestore(customerAuth.app);
}

export async function loadCheckoutIdentity(uid) {
  const db = database(uid);
  const [profile, addresses] = await Promise.all([
    getDocFromServer(doc(db, "users", uid)),
    getDocsFromServer(query(collection(db, "addresses"), where("ownerId", "==", uid))),
  ]);
  database(uid);
  return { name: profile.data()?.name || "", addresses: addresses.docs.map(d => ({ ...d.data(), id: d.id })) };
}

export async function prepareCheckout(uid, cart, location) {
  const db = database(uid);
  if (!cart.length) throw new Error("Your cart is empty.");
  const source = cart[0].storeId || "";
  if (cart.some(item => (item.storeId || "") !== source || (item.sourceType === "shop") !== Boolean(source))) throw new Error("Please order from only one shopping source at a time.");
  if (new Set(cart.map(item => item.productId)).size !== cart.length) throw new Error("Your cart contains duplicate products. Please update your cart.");
  const settings = (await getDocFromServer(doc(db, "settings", "app"))).data() || {};
  let shopFields = {};
  if (source) {
    const publicShop = await getDocFromServer(doc(db, "public_shops", source));
    if (!publicShop.exists() || publicShop.data().isActive !== true) throw new Error("This shop is unavailable.");
    const shop = (await getDocFromServer(doc(db, "shops", source))).data();
    if (!shop) throw new Error("This shop is unavailable.");
    shopFields = { storeId: source, shopNameSnapshot: String(shop.name || publicShop.data().name || ""), shopAddressSnapshot: String(shop.address || "") };
    if (Number.isFinite(shop.latitude) && Number.isFinite(shop.longitude) && Math.abs(shop.latitude) <= 90 && Math.abs(shop.longitude) <= 180) {
      Object.assign(shopFields, { pickupName: shopFields.shopNameSnapshot, pickupAddress: shopFields.shopAddressSnapshot, pickupLatitude: shop.latitude, pickupLongitude: shop.longitude });
    }
  }
  const items = await Promise.all(cart.map(async item => {
    const snapshot = await getDocFromServer(doc(db, "products", item.productId));
    const raw = snapshot.data();
    if (!source && (raw?.shopId || raw?.storeId)) throw new Error("This product's shopping source changed. Please update your cart.");
    if (!raw || raw.isActive === false || !Number.isFinite(Number(raw.price ?? raw.sellingPrice)) || Number(raw.price ?? raw.sellingPrice) < 0) throw new Error(`${item.name} is no longer available.`);
    let stock = raw.stock;
    if (source) {
      const inventory = (await getDocFromServer(doc(db, "public_shops", source, "inventory", item.productId))).data();
      if (!inventory || inventory.available !== true) throw new Error(`${item.name} is no longer available at this shop.`);
      stock = inventory.stock;
    }
    if (!Number.isFinite(stock) || !Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > stock) throw new Error(`Stock changed for ${item.name}. Please update your cart.`);
    const p = normaliseProduct(item.productId, raw);
    return { productId: item.productId, name: p.name, image: p.images[0] || "", brand: p.brand, weight: p.weight, unit: p.unit, shortDescription: p.description, price: p.price, oldPrice: p.mrp, discountPercent: p.discount, quantity: item.quantity, itemTotal: p.price * item.quantity };
  }));
  database(uid);
  const subtotal = Math.round(items.reduce((sum, item) => sum + item.itemTotal, 0) * 100) / 100;
  return { items, subtotal, deliveryCharge: deliveryCharge(settings, subtotal, location), shopFields };
}

// One document per mounted checkout, including retries after an ambiguous network failure.
export function createOrderSubmission(uid) {
  const ref = doc(collection(database(uid), "orders"));
  let pending = null, result = null, payload = null;
  return async (details, quote) => {
    if (result) return result;
    if (pending) return pending;
    database(uid);
    payload = payload || buildOrder(customerAuth.currentUser, details, quote, Timestamp.now(), `QD${Date.now()}`);
    pending = (async () => {
      try {
        await setDoc(ref, payload);
      } catch (error) {
        // A retry cannot create another document. Check whether the first write committed.
        const existing = await getDocFromServer(ref).catch(() => null);
        if (!existing?.exists() || existing.data().ownerUid !== uid || existing.data().orderId !== payload.orderId) throw error;
      }
      result = { id: ref.id, orderId: payload.orderId };
      return result;
    })();
    try { return await pending; } finally { pending = null; }
  };
}

export function checkoutError(error) {
  if (error?.code === "permission-denied") return "Checkout could not be authorized. Your cart is retained. Please contact QuickDrop before retrying.";
  if (error?.code) return "Could not confirm checkout. Check your connection. Your cart is retained; retry here rather than starting another order.";
  return error?.message || "Checkout is unavailable. Please try again.";
}
